import React from 'react';

import useIsMinBreakpoint from '@hero/react-hooks/mediaQuery/useIsMinBreakpoint';
import Button from '@hero/ui-kit/inputs/Button';
import Form, { useFormContext } from '@hero/ui-kit/inputs/Form';
import ImageUpload from '@hero/ui-kit/inputs/ImageUpload';
import Input from '@hero/ui-kit/inputs/Input';
import Option from '@hero/ui-kit/inputs/Option';
import Select from '@hero/ui-kit/inputs/Select';
import TextArea from '@hero/ui-kit/inputs/TextArea';
import Container from '@hero/ui-kit/layout/Container';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import { IAMListRole } from '../../../IAM/types';
import useCreateExternalUser from '../../api/useCreateExternalUser';
import { CreateExternalUserParams } from '../../types';
import { validationSchema } from '../../utils';
import * as Style from './style.module.scss';

interface InviteFormProps {
    closeModal: () => void;
    roles?: IAMListRole[];
}

interface FormFieldsProps {
    isPending: boolean;
    roles?: IAMListRole[];
    errorMessage?: string;
}

const FormFields: React.FC<FormFieldsProps> = ({ isPending, roles, errorMessage }) => {
    const { formState } = useFormContext();
    const isMinL = useIsMinBreakpoint('l');
    const isSubmitDisabled = isPending || !formState.isValid;

    return (
        <Section noDefaultPadding paddingHorizontal="none" paddingVertical="none">
            <H role="h5" centered>
                Create external user
            </H>

            <Container gridTemplateColumns={isMinL ? '1fr 1fr' : '1fr'} gridColumnGap="regular">
                <Input name="first_name" displayName="First name" />
                <Input name="last_name" displayName="Last name" />
            </Container>

            <Container gridTemplateColumns={'1fr'}>
                <Input name="email" displayName="Email" type="email" />
                <Select name="role" displayName="Role">
                    {(roles ?? []).map((role) => (
                        <Option key={role.id} value={role.name} labelText={role.name} />
                    ))}
                </Select>
            </Container>

            <Container gridTemplateColumns={isMinL ? '1fr 1fr' : '1fr'} gridColumnGap="regular">
                <Input name="phone" displayName="Phone" type="tel" />
                <Input name="specialty" displayName="Specialty" />
            </Container>

            <Container gridTemplateColumns={isMinL ? '1fr 2fr' : '1fr'} gridColumnGap="regular">
                <ImageUpload name="image" displayName="Profile image" />
                <TextArea name="bio" displayName="Bio" rows={3} />
            </Container>

            {errorMessage ? (
                <P className={Style.apiError} noDefaultMargin>
                    {errorMessage}
                </P>
            ) : null}

            <Section noDefaultPadding paddingTop="small" centered>
                <Button type="submit" disabled={isSubmitDisabled} width="large">
                    {isPending ? 'Creating...' : 'Create User'}
                </Button>
            </Section>
        </Section>
    );
};

const InviteForm: React.FC<InviteFormProps> = ({ closeModal, roles }) => {
    const { mutateAsync, isPending, error } = useCreateExternalUser();

    const handleSubmit = React.useCallback(
        (values: CreateExternalUserParams) => {
            mutateAsync(values)
                .then(() => {
                    closeModal();
                })
                .catch(() => {});
        },
        [mutateAsync, closeModal]
    );

    return (
        <Form
            validationSchema={validationSchema}
            submitFn={handleSubmit}
            defaultValues={{ is_active: true }}
            validationMode="onTouched"
        >
            <FormFields isPending={isPending} roles={roles} errorMessage={error?.errors[0]} />
        </Form>
    );
};

export default InviteForm;
